import { useApiQuery } from "lib/react-query";
import customFetch from "services/api";

const EMPTY_ENVELOPE = {
  thread_id: "",
  first_id: null,
  next_page_cursor: null,
  last_page: null,
  has_more: false,
  messages: [],
};

const resolvePayload = (response) => {
  if (!response) return null;
  if (response.status === true && response.data !== undefined)
    return response.data;
  if (response.data !== undefined && typeof response.data === "object")
    return response.data;
  return response;
};

export const normalizeChatEnvelope = (response) => {
  const payload = resolvePayload(response);
  if (!payload || typeof payload !== "object") {
    return { ...EMPTY_ENVELOPE };
  }

  const messages = Array.isArray(payload.messages)
    ? payload.messages
    : Array.isArray(payload.data?.messages)
      ? payload.data.messages
      : [];

  return {
    thread_id: payload.thread_id || payload.threadId || payload.id || "",
    first_id: payload.first_id ?? payload.firstId ?? null,
    next_page_cursor: payload.last_page ?? payload.lastId ?? null,
    last_page: payload.last_page ?? payload.lastId ?? null,
    has_more: Boolean(payload.has_more ?? payload.hasMore),
    messages,
  };
};

// Error code used to signal an insufficient wallet-balance response from the AI
// prompt endpoint so the chat UI can surface a specific message instead of the
// generic send-failure copy.
export const INSUFFICIENT_BALANCE_ERROR = "INSUFFICIENT_BALANCE";

// Keywords (en + ar) that mark a wallet/balance error. customFetch does NOT
// throw on HTTP errors — it returns the raw envelope after handleErrors — so we
// inspect that envelope's message/errors to classify a 422 balance failure.
const BALANCE_ERROR_KEYWORDS = [
  "enough balance",
  "wallet",
  "balance",
  "رصيد",
  "محفظة",
];

const collectErrorText = (response) => {
  const parts = [];
  if (typeof response?.message === "string") parts.push(response.message);
  const errors = response?.errors ?? response?.data?.errors;
  if (Array.isArray(errors)) {
    errors.forEach((e) => typeof e === "string" && parts.push(e));
  } else if (errors && typeof errors === "object") {
    Object.values(errors).forEach((value) => {
      if (typeof value === "string") parts.push(value);
      else if (Array.isArray(value))
        value.forEach((e) => typeof e === "string" && parts.push(e));
    });
  }
  return parts.join(" ").toLowerCase();
};

// True only for an errored envelope (status/success === false) whose message or
// errors indicate an insufficient wallet balance. Defensive: any non-object or
// non-error response returns false so the normal path is untouched.
export const isInsufficientBalanceError = (response) => {
  if (!response || typeof response !== "object") return false;
  const isError = response.status === false || response.success === false;
  if (!isError) return false;
  const text = collectErrorText(response);
  if (!text) return false;
  return BALANCE_ERROR_KEYWORDS.some((keyword) => text.includes(keyword));
};

const EMPTY_THREADS_RESULT = {
  threads: [],
  currentPage: 1,
  lastPage: 1,
  total: 0,
};

export const normalizeThreadsList = (response) => {
  const payload = resolvePayload(response);

  // Direct array (non-paginated)
  if (Array.isArray(payload)) {
    return {
      threads: payload,
      currentPage: 1,
      lastPage: 1,
      total: payload.length,
    };
  }

  // Laravel paginated response: { current_page, data: [...], last_page, total, ... }
  if (payload && Array.isArray(payload.data)) {
    return {
      threads: payload.data,
      currentPage: payload.current_page ?? 1,
      lastPage: payload.last_page ?? 1,
      total: payload.total ?? payload.data.length,
    };
  }

  if (Array.isArray(payload?.threads)) {
    return {
      threads: payload.threads,
      currentPage: 1,
      lastPage: 1,
      total: payload.threads.length,
    };
  }

  return { ...EMPTY_THREADS_RESULT };
};

// Chat messages for a given thread
export const useChatMessages = (threadId) => {
  const safeThreadId = threadId && threadId !== "null" ? threadId : null;
  const apiQuery = useApiQuery(
    safeThreadId ? `dashboard/ai/messages/${safeThreadId}` : null,
    null,
    false,
    null,
    {},
    {
      enabled: Boolean(safeThreadId),
      staleTime: 0,
      refetchOnMount: "always",
      queryKey: ["dashboard/ai/messages", safeThreadId],
      queryFn: async () => {
        if (!safeThreadId) return { status: true, data: { ...EMPTY_ENVELOPE } };
        const response = await customFetch(
          `dashboard/ai/messages/${safeThreadId}`
        );
        return { status: true, data: normalizeChatEnvelope(response) };
      },
    }
  );

  return {
    data: apiQuery.data,
    fetchLoading: apiQuery.fetchLoading,
    isFetching: apiQuery.isFetching,
    isSuccess: apiQuery.isSuccess,
    error: apiQuery.error,
    refetch: apiQuery.refetch,
  };
};

// Chat threads list (with pagination and refresh key)
export const useChatThreads = (refreshKey, page) => {
  return useApiQuery("dashboard/ai/threads", page, true, { key: refreshKey });
};

// Send a chat prompt (FormData body). On an insufficient wallet-balance 422 we
// throw a tagged error so useChatSession can surface a specific message; every
// other error keeps flowing through the existing envelope-normalization path.
export const sendChatPrompt = (body) =>
  customFetch("dashboard/ai/prompt", { method: "POST", body }).then(
    (response) => {
      if (isInsufficientBalanceError(response)) {
        const error = new Error("Insufficient wallet balance");
        error.code = INSUFFICIENT_BALANCE_ERROR;
        throw error;
      }
      return normalizeChatEnvelope(response);
    }
  );

// Delete a chat thread
export const deleteChatThread = (id) =>
  customFetch(`dashboard/ai/threads/${id}`, { method: "DELETE" });

// Load older messages (pagination)
export const fetchOlderMessages = (threadId, beforeId) =>
  customFetch(`dashboard/ai/messages/${threadId}?page=${beforeId}`).then(
    normalizeChatEnvelope
  );
